import { axiosInstance } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";

export interface DepartmentRankingEntry {
  department: string;
  members: number;
  averageScore: number;
  totalScore: number;
}

export interface IndividualUserEntry {
  _id: string;
  name: string;
  email: string;
  department: string;
  quizScore: number;
  score: number;
  completedTutorials: number;
  change: number;
}

export interface LeaderboardData {
  individualUsers: IndividualUserEntry[];
  topPerformers: DepartmentRankingEntry[];
  departmentRankings: DepartmentRankingEntry[];
}

/**
 * Hook for fetching leaderboard data
 */
export const useLeaderboardData = () => {
  return useQuery<LeaderboardData>({
    queryKey: ["leaderboard"],
    queryFn: async () => {
      const [individualRes, departmentRes] = await Promise.all([
        axiosInstance.get("/api/v1/companies/departments-top-performers"),
        axiosInstance.get("/api/v1/companies/departments-ranking"),
      ]);

      // Debug logging to understand API response structure
      console.log("🔍 Individual users API response:", individualRes.data);
      console.log("🔍 Department rankings API response:", departmentRes.data);

      // Extract individual user data from departments-top-performers
      // This endpoint returns department data with employees, so we need to flatten it
      const individualUsers: IndividualUserEntry[] = [];

      if (individualRes.data?.data && Array.isArray(individualRes.data.data)) {
        individualRes.data.data.forEach((department: any) => {
          console.log("Processing department:", department);
          if (department.employees && Array.isArray(department.employees)) {
            department.employees.forEach((employee: any) => {
              console.log("Processing employee:", employee);
              individualUsers.push({
                _id: employee._id || employee.id || Math.random().toString(),
                name: employee.fullName || employee.name || employee.fullname || "Unknown",
                email: employee.email || "",
                department: department.name || department.departmentName || "Unknown",
                quizScore: employee.quizScore || employee.score || 0,
                score: employee.quizScore || employee.score || 0,
                completedTutorials: employee.completedTutorials || employee.completedCourses || 0,
                change: employee.change || 0,
              });
            });
          } else {
            console.log("No employees found in department:", department);
          }
        });
      }

      // If no individual users were extracted, try alternative data structure
      if (individualUsers.length === 0) {
        console.log("No individual users found, trying alternative structure...");
        // Try to extract from a different response structure
        if (individualRes.data?.data && Array.isArray(individualRes.data.data)) {
          individualRes.data.data.forEach((item: any) => {
            if (item.name && item.department) {
              individualUsers.push({
                _id: item._id || item.id || Math.random().toString(),
                name: item.name || item.fullName || "Unknown",
                email: item.email || "",
                department: item.department || "Unknown",
                quizScore: item.quizScore || item.score || 0,
                score: item.quizScore || item.score || 0,
                completedTutorials: item.completedTutorials || item.completedCourses || 0,
                change: item.change || 0,
              });
            }
          });
        }
      }

      // Sort individual users by quiz score (highest first)
      const sortedIndividualUsers = individualUsers.sort((a, b) => b.quizScore - a.quizScore);

      console.log("🔍 Extracted individual users:", sortedIndividualUsers);

      // Use the department rankings directly from the API response (like the working code)
      const departmentRankings: DepartmentRankingEntry[] = departmentRes.data?.data || [];

      console.log("🔍 Department rankings from API:", departmentRankings);

      return {
        individualUsers: sortedIndividualUsers,
        topPerformers: individualRes.data?.data || [],
        departmentRankings: departmentRankings,
      };
    },
  });
};
